import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import type { WebsiteAnalyticsData } from "@/api/website-analytics-api";
import WebsiteTrafficChart from "./website-traffic-chart";
import TrendChip from "./trend-chip";
import {
  formatCompactVisits,
  formatDuration,
  formatMonthShort,
  formatPagesPerVisit,
  formatRank,
  formatRate,
  type ChartPoint,
  type TrendResult,
} from "./utils";

function MetricTile({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-md border border-border bg-card p-3">
      <div className="text-[10px] font-medium uppercase tracking-widest text-muted-foreground/70">
        {label}
      </div>
      <div className="mt-1 font-mono text-sm font-medium tabular-nums text-foreground">
        {value}
      </div>
    </div>
  );
}

function SectionLabel({ children }: { children: React.ReactNode }) {
  return (
    <div className="mb-2 flex items-center gap-3">
      <span className="text-[10px] font-semibold uppercase tracking-widest text-muted-foreground/70">
        {children}
      </span>
      <span className="h-px flex-1 bg-border" />
    </div>
  );
}

interface WebsiteAnalyticsDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  companyName: string;
  domain: string | null;
  data: WebsiteAnalyticsData;
  trend: TrendResult;
  points: ChartPoint[];
}

export default function WebsiteAnalyticsDialog({
  open,
  onOpenChange,
  companyName,
  domain,
  data,
  trend,
  points,
}: WebsiteAnalyticsDialogProps) {
  const metrics = data.metrics;

  const hasEngagement =
    metrics != null &&
    (metrics.bounce_rate !== null ||
      metrics.pages_per_visit !== null ||
      metrics.average_visit_duration_seconds !== null);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="flex max-h-[85vh] max-w-2xl flex-col gap-0 p-0">
        <DialogHeader className="flex-shrink-0 border-b border-border px-5 py-4 text-left">
          <DialogTitle className="text-base">Website Analytics</DialogTitle>
          <DialogDescription className="text-xs">
            Estimated traffic for{" "}
            <span className="font-mono text-foreground">{domain ?? "this site"}</span>.
            Third-party estimates &mdash; not {companyName}&rsquo;s own analytics.
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 space-y-6 overflow-y-auto p-5">
          <div>
            <div className="flex items-baseline gap-2">
              <span className="font-mono text-3xl font-semibold tabular-nums text-foreground">
                {formatCompactVisits(trend.current)}
              </span>
              <TrendChip trend={trend} />
            </div>
            <p className="mt-1 text-xs text-muted-foreground">
              {trend.currentMonth ? formatMonthShort(trend.currentMonth) : "Latest"}
              {trend.previousMonth && ` vs ${formatMonthShort(trend.previousMonth)}`}
              {trend.previousMonth && !trend.previousIsAdjacent && " — no data in between"}
            </p>
          </div>

          <div>
            <SectionLabel>Traffic over time</SectionLabel>
            <div className="rounded-lg border border-border bg-card p-3">
              <WebsiteTrafficChart points={points} />
            </div>
          </div>

          <div>
            <SectionLabel>Engagement</SectionLabel>
            {hasEngagement ? (
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
                <MetricTile label="Bounce rate" value={formatRate(metrics?.bounce_rate)} />
                <MetricTile
                  label="Pages / visit"
                  value={formatPagesPerVisit(metrics?.pages_per_visit)}
                />
                <MetricTile
                  label="Avg. visit duration"
                  value={formatDuration(metrics?.average_visit_duration_seconds)}
                />
                {metrics?.rank_global !== null && metrics?.rank_global !== undefined && (
                  <MetricTile label="Global rank" value={formatRank(metrics.rank_global)} />
                )}
              </div>
            ) : (
              // A row of three em-dashes reads as broken UI, so say it plainly.
              <p className="text-xs text-muted-foreground">
                No engagement metrics available for this site.
              </p>
            )}
          </div>

          <p className="text-[11px] text-muted-foreground/70">
            Source: third-party web-traffic estimates
            {data.last_fetched_at &&
              ` · Updated ${new Date(data.last_fetched_at).toLocaleDateString("en-GB", {
                day: "numeric",
                month: "short",
                year: "numeric",
              })}`}
          </p>
        </div>
      </DialogContent>
    </Dialog>
  );
}
